import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { Send, ShieldCheck, ImagePlus, Palette, Pencil, Trash2, Check, X, MessageSquare, Circle, SmilePlus, Flag, ShieldAlert, Ban } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROOM_THEMES } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import StickerPicker from "../components/StickerPicker";
import GifPicker from "../components/GifPicker";
import GiftPicker from "../components/GiftPicker";
import { toast } from "sonner";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🙏", "🫶"];
const CONFETTI_COLORS = ["#E87FA3", "#86C79A", "#F2CE63", "#C6ADEA", "#F2A98B"];

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function giftBurst() {
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.7 }, colors: CONFETTI_COLORS, scalar: 1.1 });
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("general");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [online, setOnline] = useState(0);
  const [typers, setTypers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [theme, setTheme] = useState(() => localStorage.getItem("haplap_room_theme") || "blossom");
  const [lightbox, setLightbox] = useState(null);
  const endRef = useRef();
  const listRef = useRef();
  const fileRef = useRef();
  const taRef = useRef();
  const seen = useRef(new Set());
  const inited = useRef(false);
  const typingRef = useRef(0);
  const themeObj = ROOM_THEMES.find((t) => t.id === theme) || ROOM_THEMES[0];

  useEffect(() => { api.get("/chat/channels").then((r) => setChannels(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    api.get(`/chat?channel=${active}`).then((r) => {
      const data = r.data;
      const fresh = data.filter((m) => m.gift && !seen.current.has(m.id));
      data.forEach((m) => seen.current.add(m.id));
      if (inited.current && fresh.length) giftBurst();
      inited.current = true;
      setMsgs(data);
    }).catch(() => {});
  }, [active]);

  useEffect(() => {
    seen.current = new Set(); inited.current = false; setMsgs([]);
    load();
    const i = setInterval(load, 3000);
    return () => clearInterval(i);
  }, [active, load]);

  useEffect(() => {
    const ping = () => api.post("/chat/presence").then((r) => setOnline(r.data.online)).catch(() => {});
    ping();
    const i = setInterval(ping, 25000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const p = () => api.get(`/chat/typing?channel=${active}`).then((r) => setTypers(r.data.names || [])).catch(() => {});
    const i = setInterval(p, 3000);
    return () => clearInterval(i);
  }, [active]);

  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs, typers]);
  const growTextarea = (el) => { if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 144) + "px"; } };

  const pickTheme = (id) => { setTheme(id); localStorage.setItem("haplap_room_theme", id); };

  const onType = (v) => {
    setText(v);
    const now = Date.now();
    if (now - typingRef.current > 2000) { typingRef.current = now; api.post("/chat/typing", { channel: active }).catch(() => {}); }
  };

  const send = async (extra) => {
    const body = { channel: active, ...(extra || { text }) };
    if (!extra && !text.trim()) return;
    try {
      const { data } = await api.post("/chat", body);
      seen.current.add(data.id);
      setMsgs((m) => [...m, data]);
      setText("");
      if (taRef.current) taRef.current.style.height = "auto";
      if (body.gift_id) giftBurst();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: data.url, media_type: data.media_type });
      }
      await send({ text: "", media_items: uploaded });
      toast.success(uploaded.length > 1 ? `Shared ${uploaded.length} photos 💛` : "Shared 💛");
    } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  const saveEdit = async (id) => {
    try {
      const { data } = await api.put(`/chat/${id}`, { text: editText });
      setMsgs((m) => m.map((x) => (x.id === id ? data : x)));
      setEditing(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/chat/${id}`); setMsgs((m) => m.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const react = async (id, emoji) => {
    try { const { data } = await api.post(`/chat/${id}/react`, { emoji }); setMsgs((m) => m.map((x) => (x.id === id ? { ...x, reactions: data.reactions } : x))); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const report = async (id) => {
    try { await api.post(`/chat/${id}/report`, { reason: "Reported from Community Room" }); toast.success("Reported to moderators 🛡️ Thank you for keeping us safe."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const isMod = user && (user.role === "admin" || user.is_guardian);
  const block = async (uid) => {
    if (!window.confirm("Block this person? They won't be able to message or interact with you.")) return;
    try { await api.post(`/block/${uid}`); toast.success("Blocked. You won't hear from them again."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const suspend = async (uid) => {
    if (!window.confirm("Put this member on a 24-hour cool-down while our team reviews? Misusing this power will remove your own account.")) return;
    try { await api.post(`/moderate/suspend/${uid}`, { hours: 24, reason: "Guardian cool-down (chat)" }); toast.success("Member suspended for 24h. Admins will review. 🛡️"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const activeCh = channels.find((c) => c.id === active);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-copper">Community Room</p>
          <h1 className="font-serif text-3xl font-bold text-bronze">Everyone, together 💛</h1>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="online-count" className="flex items-center gap-1.5 text-sm text-sage bg-sage/10 rounded-full px-3 py-1.5">
            <Circle className="w-2.5 h-2.5 fill-sage text-sage animate-pulse" /> {online} online
          </span>
          <Button data-testid="open-dms-btn" variant="outline" onClick={() => navigate("/messages")} className="rounded-full">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Private
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="room-theme-btn" variant="outline" size="icon" className="rounded-full"><Palette className="w-4 h-4" /></Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 rounded-2xl">
              <p className="text-xs font-mono uppercase tracking-widest text-copper mb-2">Room theme</p>
              <div className="grid grid-cols-1 gap-1.5">
                {ROOM_THEMES.map((t) => (
                  <button key={t.id} data-testid={`room-theme-${t.id}`} onClick={() => pickTheme(t.id)}
                    className={`flex items-center gap-2 p-2 rounded-xl border ${theme === t.id ? "border-copper" : "border-transparent"} hover:bg-secondary`}>
                    <span className={`w-8 h-8 rounded-lg ${t.class}`} />
                    <span className="text-sm">{t.name}</span>
                    {theme === t.id && <Check className="w-4 h-4 text-copper ml-auto" />}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-sage bg-sage/10 rounded-full px-3 py-1.5 w-fit mb-4">
        <ShieldCheck className="w-3.5 h-3.5" /> Be kind. Harassment or hate speech means an instant, permanent ban.
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1">
          <div className="bg-card rounded-2xl border border-border p-2 space-y-1" data-testid="channel-list">
            {channels.map((c) => (
              <button key={c.id} data-testid={`channel-${c.id}`} onClick={() => setActive(c.id)}
                className={`w-full flex items-center gap-2 p-2.5 rounded-xl text-left transition-colors ${active === c.id ? "bg-copper/15 text-copper" : "hover:bg-secondary"}`}>
                <span className="text-lg">{c.emoji}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{c.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{c.desc}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">{c.message_count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="rounded-3xl border border-border flex flex-col h-[64vh] overflow-hidden" data-testid="community-room">
            <div className="px-4 py-2.5 border-b border-border bg-card flex items-center gap-2">
              <span className="text-lg">{activeCh?.emoji}</span>
              <div>
                <p className="font-serif font-semibold text-bronze leading-tight">{activeCh?.name || "General"}</p>
                <p className="text-[11px] text-muted-foreground">{activeCh?.desc}</p>
              </div>
            </div>

            <div ref={listRef} className={`flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar ${themeObj.class}`} data-testid="chat-messages">
              {msgs.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Be the first to say something kind here.</p>}
              {msgs.map((m) => {
                const mine = m.user_id === user?.user_id;
                const canDelete = mine || user?.role === "admin" || user?.is_guardian;
                const isText = m.text && !m.gift && !m.media_url && !m.sticker && !m.gif;
                return (
                  <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`group flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                    <img src={fileUrl(m.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${m.user_id}`} alt="" className="w-8 h-8 rounded-full bg-secondary shrink-0" />
                    <div className={`max-w-[75%] ${mine ? "items-end flex flex-col" : ""}`}>
                      <p className={`text-xs text-muted-foreground mb-0.5 ${mine ? "text-right" : ""}`}>{m.name} · {timeAgo(m.created_at)}{m.edited && " · edited"}</p>
                      {editing === m.id ? (
                        <div className="flex items-center gap-1">
                          <Input value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(m.id)}
                            data-testid="chat-edit-input" className="rounded-full h-8 text-sm" autoFocus />
                          <button onClick={() => saveEdit(m.id)} data-testid="chat-edit-save" className="text-sage p-1"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(null)} className="text-muted-foreground p-1"><X className="w-4 h-4" /></button>
                        </div>
                      ) : m.gift ? (
                        <div data-testid="gift-message" className="rounded-2xl px-4 py-3 bg-penny/15 border border-penny/30 text-center gift-pop">
                          <div className="text-4xl">{m.gift.emoji}</div>
                          <p className="text-xs text-bronze font-medium mt-1">sent a {m.gift.name}</p>
                        </div>
                      ) : (m.media_items && m.media_items.length) || m.media_url ? (
                        (() => {
                          const items = m.media_items && m.media_items.length ? m.media_items : [{ url: m.media_url, media_type: m.media_type }];
                          if (items.length === 1) {
                            const it = items[0];
                            return it.media_type === "video"
                              ? <video src={fileUrl(it.url)} controls className="rounded-xl max-h-56" />
                              : <img src={fileUrl(it.url)} alt="shared" onClick={() => setLightbox(fileUrl(it.url))} className="rounded-xl max-h-56 object-cover cursor-zoom-in" />;
                          }
                          return (
                            <div className="grid grid-cols-2 gap-1.5 max-w-[16rem]" data-testid={`chat-album-${m.id}`}>
                              {items.map((it, i) => it.media_type === "video"
                                ? <video key={i} src={fileUrl(it.url)} controls className="rounded-lg h-28 w-full object-cover" />
                                : <img key={i} src={fileUrl(it.url)} alt="" onClick={() => setLightbox(fileUrl(it.url))} className="rounded-lg h-28 w-full object-cover cursor-zoom-in" />)}
                            </div>
                          );
                        })()
                      ) : m.gif ? (
                        <img src={fileUrl(m.gif)} alt="gif" className="rounded-xl max-h-40" />
                      ) : m.sticker ? (
                        <div className="text-4xl">{m.sticker}</div>
                      ) : (
                        <div className={`rounded-2xl px-3 py-2 text-sm ${mine ? "bg-copper text-white" : "bg-card border border-border"}`}>{m.text}</div>
                      )}
                      {m.reactions && Object.keys(m.reactions).length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : ""}`}>
                          {Object.entries(m.reactions).map(([em, users]) => (
                            <button key={em} data-testid={`reaction-${m.id}-${em}`} onClick={() => react(m.id, em)}
                              className={`text-xs px-1.5 py-0.5 rounded-full border ${users.includes(user?.user_id) ? "bg-copper/15 border-copper/40" : "bg-secondary border-transparent"}`}>{em} {users.length}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    {editing !== m.id && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity self-center">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button data-testid={`chat-react-${m.id}`} className="text-muted-foreground hover:text-copper p-1"><SmilePlus className="w-3.5 h-3.5" /></button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-1.5 rounded-full flex gap-1">
                            {QUICK_REACTIONS.map((em) => (
                              <button key={em} data-testid={`react-opt-${m.id}-${em}`} onClick={() => react(m.id, em)} className="text-lg p-1 rounded-full hover:bg-secondary">{em}</button>
                            ))}
                          </PopoverContent>
                        </Popover>
                        {mine && isText && (
                          <button data-testid={`chat-edit-${m.id}`} onClick={() => { setEditing(m.id); setEditText(m.text); }} className="text-muted-foreground hover:text-copper p-1"><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                        {!mine && (
                          <button data-testid={`chat-report-${m.id}`} onClick={() => report(m.id)} className="text-muted-foreground hover:text-destructive p-1"><Flag className="w-3.5 h-3.5" /></button>
                        )}
                        {!mine && (
                          <button data-testid={`chat-block-${m.id}`} onClick={() => block(m.user_id)} title="Block" className="text-muted-foreground hover:text-destructive p-1"><Ban className="w-3.5 h-3.5" /></button>
                        )}
                        {isMod && !mine && (
                          <button data-testid={`chat-suspend-${m.id}`} onClick={() => suspend(m.user_id)} title="Cool-down (24h)" className="text-muted-foreground hover:text-copper p-1"><ShieldAlert className="w-3.5 h-3.5" /></button>
                        )}
                        {canDelete && (
                          <button data-testid={`chat-delete-${m.id}`} onClick={() => del(m.id)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {typers.length > 0 && (
                <p data-testid="typing-indicator" className="text-xs text-muted-foreground italic px-1">
                  {typers.slice(0, 3).join(", ")} {typers.length === 1 ? "is" : "are"} typing<span className="typing-dots">…</span>
                </p>
              )}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-border bg-card flex items-end gap-1.5">
              <StickerPicker onPick={(s) => send({ text: "", sticker: s })} />
              <GifPicker onPick={(url) => send({ text: "", gif: url })} />
              <GiftPicker onPick={(gift_id) => send({ text: "", gift_id })} />
              <button type="button" data-testid="chat-upload-btn" onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-secondary text-sage shrink-0"><ImagePlus className="w-5 h-5" /></button>
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={upload} data-testid="chat-file-input" />
              <textarea data-testid="chat-input" ref={taRef} value={text} rows={1}
                onChange={(e) => { onType(e.target.value); growTextarea(e.target); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Say something kind…  (Shift + Enter for a new line)"
                className="flex-1 resize-none rounded-2xl border border-border bg-background px-4 py-2.5 text-sm leading-relaxed max-h-36 overflow-y-auto focus:outline-none focus:ring-2 focus:ring-copper/40" />
              <Button data-testid="chat-send" onClick={() => send()} size="icon" className="bg-copper hover:bg-copper/90 rounded-full shrink-0"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <div data-testid="chat-lightbox" onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 cursor-zoom-out">
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[92vw] rounded-xl object-contain shadow-2xl" />
          <button onClick={() => setLightbox(null)} data-testid="chat-lightbox-close" className="absolute top-4 right-4 text-white/90 hover:text-white"><X className="w-7 h-7" /></button>
        </div>
      )}
    </div>
  );
}
